import type { AmhsLiveReportPayload } from '../../types/amhsStream';
import type { AmhsKpiBlock } from '../../types/simulationPlayback';

export function normalizeLiveReport(value: unknown): AmhsLiveReportPayload | null {
  if (!value || typeof value !== 'object') return null;
  return value as AmhsLiveReportPayload;
}

export function liveReportKpis(report: AmhsLiveReportPayload | null | undefined): AmhsKpiBlock | null {
  if (!report) return null;
  const kpis = report.finalSimulationKpis ?? report.report?.simulation?.kpis;
  if (!kpis || typeof kpis !== 'object') return null;
  return kpis as AmhsKpiBlock;
}

export function liveReportRunStatus(
  report: AmhsLiveReportPayload | null | undefined
): 'running' | 'final' | null {
  const status = report?.report?.runStatus;
  if (status === 'running' || status === 'final') return status;
  return null;
}

function fmt(n: unknown, digits = 1): string {
  if (typeof n !== 'number' || !Number.isFinite(n)) return '—';
  return n.toLocaleString(undefined, {
    maximumFractionDigits: digits,
    minimumFractionDigits: Number.isInteger(n) ? 0 : Math.min(digits, 1),
  });
}

function util(n: unknown): string {
  if (typeof n !== 'number' || !Number.isFinite(n)) return '—';
  const fraction = n <= 1 ? n : n / 100;
  return `${fmt(fraction * 100, 1)}%`;
}

export type LiveDispatchMetric = {
  label: string;
  value: string;
};

export type DispatchComparisonRowDef = {
  id: string;
  label: string;
  hint?: string;
  /** Extract numeric KPI for highlighting; null skips best-column highlight. */
  raw: (kpis: AmhsKpiBlock) => number | null;
  format: (kpis: AmhsKpiBlock) => string;
  higherIsBetter: boolean;
};

/** Rows for Nearest | FIFO | OR-Tools comparison table. */
export const DISPATCH_COMPARISON_ROWS: DispatchComparisonRowDef[] = [
  {
    id: 'ohtUtilization',
    label: 'OHT utilization',
    hint: 'Busy ÷ (makespan × vehicles)',
    raw: (k) => (typeof k.ohtUtilization === 'number' ? k.ohtUtilization : null),
    format: (k) => util(k.ohtUtilization),
    higherIsBetter: true,
  },
  {
    id: 'ohtProductiveUtilization',
    label: 'Productive utilization',
    raw: (k) =>
      typeof k.ohtProductiveUtilization === 'number' ? k.ohtProductiveUtilization : null,
    format: (k) => util(k.ohtProductiveUtilization),
    higherIsBetter: true,
  },
  {
    id: 'emptyTravelShare',
    label: 'Empty travel share',
    raw: (k) => (typeof k.emptyTravelShare === 'number' ? k.emptyTravelShare : null),
    format: (k) => util(k.emptyTravelShare),
    higherIsBetter: false,
  },
  {
    id: 'avgDeliveryS',
    label: 'Avg delivery (s)',
    raw: (k) => (typeof k.avgDeliveryS === 'number' ? k.avgDeliveryS : null),
    format: (k) => `${fmt(k.avgDeliveryS)} s`,
    higherIsBetter: false,
  },
  {
    id: 'avgWaitS',
    label: 'Avg wait (s)',
    raw: (k) => (typeof k.avgWaitS === 'number' ? k.avgWaitS : null),
    format: (k) => `${fmt(k.avgWaitS)} s`,
    higherIsBetter: false,
  },
  {
    id: 'avgWaitForVehicleS',
    label: 'Wait for vehicle (s)',
    raw: (k) => (typeof k.avgWaitForVehicleS === 'number' ? k.avgWaitForVehicleS : null),
    format: (k) => `${fmt(k.avgWaitForVehicleS)} s`,
    higherIsBetter: false,
  },
  {
    id: 'completedRequests',
    label: 'Completed TRs',
    raw: (k) => (typeof k.completedRequests === 'number' ? k.completedRequests : null),
    format: (k) => fmt(k.completedRequests, 0),
    higherIsBetter: true,
  },
  {
    id: 'throughput',
    label: 'Throughput',
    raw: (k) => {
      if (typeof k.foupThroughputPerHour === 'number') return k.foupThroughputPerHour;
      if (typeof k.hopThroughputPerHour === 'number') return k.hopThroughputPerHour;
      return null;
    },
    format: (k) => {
      if (typeof k.foupThroughputPerHour === 'number') {
        return `${fmt(k.foupThroughputPerHour)} FOUP/h`;
      }
      if (typeof k.hopThroughputPerHour === 'number') {
        return `${fmt(k.hopThroughputPerHour)} hops/h`;
      }
      return '—';
    },
    higherIsBetter: true,
  },
  {
    id: 'avgCycleTimeS',
    label: 'Avg cycle (s)',
    raw: (k) => (typeof k.avgCycleTimeS === 'number' ? k.avgCycleTimeS : null),
    format: (k) => `${fmt(k.avgCycleTimeS)} s`,
    higherIsBetter: false,
  },
  {
    id: 'deliveryP95S',
    label: 'Delivery P95 (s)',
    raw: (k) => (typeof k.deliveryP95S === 'number' ? k.deliveryP95S : null),
    format: (k) => `${fmt(k.deliveryP95S)} s`,
    higherIsBetter: false,
  },
];

export function bestComparisonColumn(
  row: DispatchComparisonRowDef,
  values: Partial<Record<string, number | null>>
): string | null {
  const entries = Object.entries(values).filter(
    (entry): entry is [string, number] =>
      typeof entry[1] === 'number' && Number.isFinite(entry[1])
  );
  if (entries.length < 2) return null;
  const best = row.higherIsBetter
    ? entries.reduce((a, b) => (b[1] > a[1] ? b : a))
    : entries.reduce((a, b) => (b[1] < a[1] ? b : a));
  return best[0];
}

/** Key dispatch KPIs for the simulation panel (matches batch report focus metrics). */
export function liveDispatchMetrics(kpis: AmhsKpiBlock | null): LiveDispatchMetric[] {
  if (!kpis) return [];
  return [
    { label: 'OHT utilization', value: util(kpis.ohtUtilization) },
    { label: 'Empty travel share', value: util(kpis.emptyTravelShare) },
    { label: 'Avg wait', value: `${fmt(kpis.avgWaitS)} s` },
    {
      label: 'Throughput',
      value:
        typeof kpis.foupThroughputPerHour === 'number'
          ? `${fmt(kpis.foupThroughputPerHour)} FOUP/h`
          : typeof kpis.hopThroughputPerHour === 'number'
            ? `${fmt(kpis.hopThroughputPerHour)} hops/h`
            : '—',
    },
  ].filter((row) => row.value !== '— s' && row.value !== '—');
}
